import { Location } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component, DestroyRef, inject, Inject, OnInit, Optional,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { AdminService } from '../../admin/services/admin.service';
import { BudgetViewComponent } from '../../budget/budget-view/budget-view.component';
import { RoleService } from '../../role/role.service';
import { CheckInService } from '../../services/check-in.service';
import { PDFService } from '../../services/pdf.service';
import { StatusesService } from '../../services/statuses.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { OrderHistoryComponent } from '../order-history/order-history.component';
import { OrderNotificationsComponent } from '../order-notifications/order-notifications.component';
import { catchError, combineLatest, EMPTY, first, firstValueFrom } from 'rxjs';
import { FirebaseService } from '../../services/firebase.service';
import { CheckIn } from '../../models/checkin.model';
import { ChatService } from '../../chat/chat.service';
import { IndexedDBService } from '../../services/indexed-db.service';

@Component({
    selector: 'app-order',
    templateUrl: './order.component.html',
    styleUrls: ['./order.component.scss'],
    standalone: false
})
export class OrderComponent implements OnInit {
  order = signal<CheckIn | undefined>(undefined);

  editMode = signal(false);

  currentImage = null;

  allowedDocumentsForCreation = signal<string[]>([]);

  company_logo;

  statuses = [];

  company: any = {};

  forbidden = signal(false);

  creatingBudget = false;

  budgetBack:any;

  heading;

  id

  expandedComment;

  updatingOrder = signal(false);

  creatingWork = signal(false);

  private readonly _destroyRef = inject(DestroyRef);

  chatService = inject(ChatService);

  private router = inject(Router);

  constructor(
    private checkinService: CheckInService,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private statusesService: StatusesService,
    private dialog: MatDialog,
    public roleService: RoleService,
    private adminService: AdminService,
    private pdfService: PDFService,
    private snackBar: MatSnackBar,
    public location: Location,
    private cdr: ChangeDetectorRef,
    private firebaseService: FirebaseService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data,
    @Optional() private matDialogRef: MatDialogRef<OrderComponent>,
  ) {
    if (typeof this.data === 'number' || typeof this.data === 'string') {
      this.id = this.data;
      this.load().then(() => {});
    }
    else if ((typeof this.data === 'object') && Object.keys(this.data).length > 0) {
      this.id = this.data.id;
      this.load().then(() => {});
    }
  }

  ngOnInit() {
    this.company_logo = this.adminService.local$.getValue().preferences.logo || 'assets/images/kongu.png';
    this.company = this.adminService.local$.getValue();
    this.activatedRoute.params.forEach((params) => {
      if (params.id) {
        this.adminService.pageTitle.set(this.adminService.getText('order_plural', 'capitalize'));
        this.id = params.id;
        this.load().then(() => {});
      }
    });
    this.activatedRoute.queryParams.forEach((params) => {
      if (params.budget) {
        this.budgetBack = params.budget;
      }
      if(params.goto && params.goto === 'comments') {
        this.expandedComment = params.id;
      }
    });
    this.statusesService.all('Status::Order').toPromise().then((value:any) => {
      this.statuses = value.body;
    });
    this.firebaseService.subscribeToMessages(
      'updated-document',
      { id: this.id, type: 'CheckIn' },
      this.handleUpdatedOrderMessage.bind(this),
      this._destroyRef
    );

    
    combineLatest([
      this.roleService.can('create', 'inventory_action'),
      this.roleService.can('create', 'invoice'),
      this.roleService.can('create', 'budget'),
    ])
      .subscribe(([canCreateInventoryAction, canCreateInvoice, canCreateBudget]) => {
          const documents = [];
          if (canCreateInventoryAction) {
            documents.push('InventoryAction::Pricing', 'InventoryAction::BuyOrder');
          }
          if (canCreateInvoice) {
            documents.push('Invoice::Sale');
          }
          if (canCreateBudget) {
            documents.push('Budget');
          }
  
          this.allowedDocumentsForCreation.set(documents);
        });
  }

  load() {
    return new Promise((resolve, reject) => {
      this.checkinService.get(this.id).toPromise().then((response: HttpResponse<CheckIn>) => {
        this.order.set(response.body);
        this.addChat({ switchToChatView: false });
        resolve(this.order());
      }).catch((error:HttpErrorResponse) => {
        if (error.status === 403) {
          this.forbidden.set(true);
        }
        reject(error);
      });
    });
  }

  reload() {
    this.editMode.set(false);
    this.load().then(() => {});
  }

  goBack() {
    if (this.matDialogRef instanceof MatDialogRef) {
      this.matDialogRef.close();
    } else {
      this.location.back();
    }
  }

  print() {
    this.pdfService.getOrder(this.order().id, `Order_de_trabajo_${this.order().correlative_id}`);
  }

  setStatus(status: number) {
    this.dialog.open(ConfirmDialogComponent, { data: { title: 'Actualizar Estado' } }).afterClosed().toPromise().then((result) => {
      if (result) {
        this.order().status_id = status;
        this.updatingOrder.set(true);
        firstValueFrom(this.checkinService.update(this.id, { check_in: { status_id: status } })).catch((error:HttpErrorResponse) => {
          console.log(error);
        });
      }
    });
  }

  openNotifications() {
    this.dialog.open(OrderNotificationsComponent, { data: { order_id: this.order().id } });
  }

  openChanges() {
    this.dialog.open(OrderHistoryComponent, { data: { order_id: this.order().id, works: this.order().works }, minWidth: '60%' });
  }

  sanitizeBackground(url) {
    return this.sanitizer.bypassSecurityTrustStyle(`url('${url}')`);
  }

  getTotal(useTax = true) {
    const total = this.order().total + this.order().budgets.reduce((acc, budget) => acc + budget.total, 0);
    if (useTax) {
      return total;
    }
    const tax = this.company.country.preferences.tax_rate;
    return Math.round(total / tax);
  }

  get requirementTotal() {
    return this.order().inventory_action_for_consume?.total * this.adminService.getTaxValue() || 0
  }

  budgetItems(budget) {
    return budget.inventory_actions.flatMap((action) => action.inventory_action_products.map((product) => ({
      ...product,
      comment: action.comment,
    })));
  }

  openBudget(budgetId: number) {
    this.dialog.open(BudgetViewComponent, {
      data: { id: budgetId },
      minWidth: '90%',
    }).afterClosed().toPromise().then(() => {
      this.load().then(() => {});
    }
    );
  }

  addChatLoading = signal(false);

  addChat({ switchToChatView = true } = {}) {
    this.addChatLoading.set(true);
    
    this.chatService.addChat("Order", this.order().id, switchToChatView).pipe(
      first(),
      catchError(() => {
        this.addChatLoading.set(false);
        this.snackBar.open(
          'Error al agregar el chat',
          'Cerrar',
          { duration: 3000, panelClass: ['snackbar-error'] },
        );
        return EMPTY;
      }),
    ).subscribe(() => {
      if (this.matDialogRef instanceof MatDialogRef && switchToChatView) {
        this.matDialogRef.close();
      }
      this.addChatLoading.set(false);
    });
  }

  getOrderTotal(): number {
    const inventoryTotal = (this.order().inventory_action_for_consume?.total || 0) * 1.19;

    const budgetsTotal = this.order().budgets?.reduce((sum, budget) => sum + (budget.total * 1.19), 0) || 0;

    return inventoryTotal + budgetsTotal;
  }

  private handleUpdatedOrderMessage() {
    this.load().then(() => {
      this.updatingOrder.set(false);
      this.snackBar.open('La orden ha sido actualizada', 'Cerrar', { duration: 5000 });
    });
  }
}
